// A steam bench's profile edited on the canvas, in the drawing editor
// (ui/drawingEditor.ts) as one closed polygon: the wall stands on its left, the
// floor under it, and the profile is held against both. Opened from Tools ▸
// Steam bench presets…; docs/claude/benches.md, *The profile editor*.
import {
  type BenchProfilePoint,
  type GeomEntity,
  type Polygon,
  type ProductInstance,
  type Vec2,
} from '../../shared/types';
import {
  floorAndWallShift,
  normalizeProfile,
  outlineProfile,
  type ProfileArc,
  profileExtent,
  profileOutline,
  profileOutlineProblem,
  profileSeatHeight,
} from '../benchProfile';
import { tr } from '../i18n';
import { setTool } from '../main';
import {
  flashToolStatus,
  newId,
} from './core';
import {
  type DrawEditHost,
  openDrawEditSession,
} from './drawingEditor';
import {
  infoRow,
  sectionTitle,
} from './panelRows';

/** The wall's and the floor's thickness as the editor draws them (mm). */
const GUIDE = 150;
/** The editor's instance stands in the corner where wall and floor meet, so
 *  its label sits on them; its local frame is the profile's moved by this. */
const ORIGIN: Vec2 = { x: -GUIDE / 2, y: -GUIDE / 2 };

const toLocal = (p: Vec2): Vec2 => ({ x: p.x - ORIGIN.x, y: p.y - ORIGIN.y });
const toProfile = (p: Vec2): Vec2 => ({ x: p.x + ORIGIN.x, y: p.y + ORIGIN.y });

/** Diagonal hatch lines across a rectangle (the drawing convention for a cut
 *  wall or floor), `step` apart. */
function hatchLines(x0: number, y0: number, x1: number, y1: number, step: number, color: string): GeomEntity[] {
  const out: GeomEntity[] = [];
  for (let c = Math.ceil((y0 - x1) / step) * step; c <= y1 - x0; c += step) {
    const xa = Math.max(x0, y0 - c), xb = Math.min(x1, y1 - c);
    if (xb - xa > 1e-6) out.push({ kind: 'line', a: toLocal({ x: xa, y: xa + c }), b: toLocal({ x: xb, y: xb + c }), color });
  }
  return out;
}

/** The wall (x ≤ 0) and the floor (y ≤ 0) round a profile `w` deep, `h` tall,
 *  in the editor instance's local frame: filled, hatched, their faces drawn
 *  and each named. */
function guides(w: number, h: number): GeomEntity[] {
  const X = Math.max(1200, w + 500), Y = Math.max(1100, h + 500);
  const rect = (x0: number, y0: number, x1: number, y1: number): Vec2[] =>
    [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }].map(toLocal);
  const ink = '#aeb6c2', face = '#d5dbe3';
  return [
    { kind: 'hatch', loops: [rect(-GUIDE, -GUIDE, 0, Y)], solid: true, color: '#5c6573b3' },
    { kind: 'hatch', loops: [rect(0, -GUIDE, X, 0)], solid: true, color: '#5c6573b3' },
    ...hatchLines(-GUIDE, 0, 0, Y, 60, ink),
    ...hatchLines(-GUIDE, -GUIDE, X, 0, 60, ink),
    { kind: 'line', a: toLocal({ x: 0, y: 0 }), b: toLocal({ x: 0, y: Y }), color: face },
    { kind: 'line', a: toLocal({ x: 0, y: 0 }), b: toLocal({ x: X, y: 0 }), color: face },
    { kind: 'text', pos: toLocal({ x: -GUIDE / 2, y: Y + 40 }), height: 70, value: tr('Wall'), align: 'center', color: face },
    { kind: 'text', pos: toLocal({ x: X - 40, y: -GUIDE - 40 }), height: 70, value: tr('Floor'), align: 'right', baseline: 'top', color: face },
  ];
}

/** The profile's outlines and open lines as the editor holds them, in the
 *  profile's frame. */
function drawn(inst: ProductInstance): { outlines: Array<{ points: Vec2[]; holes?: Vec2[][] }>; openLines: number } {
  return {
    outlines: (inst.drawPolygons ?? []).map((p) => ({ points: p.points.map(toProfile), holes: p.holes?.map((h) => h.map(toProfile)) })),
    openLines: inst.drawWalls?.length ?? 0,
  };
}

/** Edit `profile` (empty: draw a new one) on the canvas; `done` gets the
 *  profile drawn, on the floor against the wall, and `reopen` shows the
 *  presets window again, whether it was done or cancelled. */
export function editSteamProfile(name: string, profile: readonly BenchProfilePoint[], done: (p: BenchProfilePoint[]) => void, reopen: () => void): void {
  const { points, arcs } = profile.length >= 3 ? profileOutline(profile) : { points: [] as Vec2[], arcs: [] as ProfileArc[] };
  const ext = points.length ? profileExtent(profile) : { width: 0, height: 0 };
  const poly: Polygon | null = points.length
    ? { id: newId('poly'), points: points.map(toLocal), fill: '#d9d4cb', fillOpacity: 1, stroke: '#5b5348', strokeOpacity: 1, z: 0 }
    : null;
  const inst: ProductInstance = {
    id: `profile-${newId('edit')}`,
    templateId: 'profile-edit',
    name,
    category: 'custom',
    x: ORIGIN.x,
    y: ORIGIN.y,
    rotation: 0,
    width: 1,
    depth: 1,
    color: '#5b5348',
    drawPolygons: poly ? [poly] : [],
    preparations: [],
    properties: [],
  };
  const host: DrawEditHost = {
    closedOnly: true,
    finish(i) {
      const d = drawn(i);
      const why = profileOutlineProblem(d.outlines, d.openLines);
      if (why) return why;
      done(normalizeProfile(outlineProfile(d.outlines[0].points, arcs)));
      return null;
    },
    reopen,
    reconcile(i) {
      const polys = i.drawPolygons ?? [];
      const all = polys.flatMap((p) => [...p.points, ...(p.holes ?? []).flat()]).map(toProfile);
      const { dx, dy } = floorAndWallShift(all);
      if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return false;
      const shift = (q: Vec2): Vec2 => ({ x: q.x + dx, y: q.y + dy });
      for (const p of polys) {
        p.points = p.points.map(shift);
        if (p.holes) p.holes = p.holes.map((h) => h.map(shift));
      }
      // Say why it moved: a shape jumping on its own is a surprise otherwise.
      if (Math.hypot(dx, dy) > 0.5) flashToolStatus(tr('Moved onto the floor, against the wall.'));
      return true;
    },
    panel(body, i) {
      body.appendChild(sectionTitle('Steam bench profile'));
      const d = drawn(i);
      const why = profileOutlineProblem(d.outlines, d.openLines);
      if (!why) {
        const prof = outlineProfile(d.outlines[0].points, arcs);
        const e = profileExtent(prof);
        body.appendChild(infoRow('Depth (mm)', String(Math.round(e.width))));
        body.appendChild(infoRow('Seat height (mm)', String(Math.round(profileSeatHeight(prof)))));
        body.appendChild(infoRow('Overall height (mm)', String(Math.round(e.height))));
      }
      const note = document.createElement('div');
      note.className = 'prop-empty';
      note.textContent = tr(why ?? 'The profile stands on the floor against the wall: whatever is drawn moves to touch both.');
      body.appendChild(note);
    },
    banner: (i) => ({
      crumb: tr('Bench profiles'),
      title: i.name,
      hint: tr('Draw with Polygon or Rectangle · double-click to edit corners'),
    }),
    dims(i) {
      const d = drawn(i);
      if (profileOutlineProblem(d.outlines, d.openLines)) return [];
      const prof = outlineProfile(d.outlines[0].points, arcs);
      const e = profileExtent(prof), seat = profileSeatHeight(prof);
      return [
        { a: { x: 0, y: 0 }, b: { x: e.width, y: 0 } },
        { a: { x: 0, y: 0 }, b: { x: 0, y: e.height } },
        ...(Math.abs(seat - e.height) > 1 ? [{ a: { x: e.width, y: 0 }, b: { x: e.width, y: seat } }] : []),
      ];
    },
  };
  const W = Math.max(1200, ext.width + 500), H = Math.max(1100, ext.height + 500);
  // Room round it for the figures and the wall's and floor's names.
  openDrawEditSession(host, inst, { min: { x: -GUIDE - 300, y: -GUIDE - 300 }, max: { x: W, y: H + 150 } });
  // Set after entering: the editor would make the wall and floor shapes to edit.
  inst.geometry = { entities: guides(ext.width, ext.height), bounds: { min: toLocal({ x: -GUIDE, y: -GUIDE }), max: toLocal({ x: W, y: H }) } };
  if (!poly) setTool('polygon');
}
